import Close20 from '@carbon/icons-react/es/close/20';
import { Col, Row as AntRow, Tooltip } from 'antd';
import { FormListOperation } from 'antd/es/form/FormList';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import styled from 'styled-components';
import { FieldSectionType } from 'api/field-sections/types';
import { ProjectStatusGroup, TaskStatusGroup } from 'api/tasks';
import { Button } from 'components/atoms/button';
import { DragHandle } from 'components/atoms/drag-handle';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { StatusPickerOption } from 'components/atoms/status-picker/option';
import { StatusIndicatorIcon } from 'components/molecules/status-indicator';
import { StatusSelect } from 'components/molecules/status-select';
import { useTranslation } from 'hooks/use-translation';
import { FieldOption } from '../types';
import {
  projectStatusGroupOptions,
  projectStatusGroupOptionsLookup,
  taskStatusGroupOptions,
  taskStatusGroupOptionsLookup,
} from '../utils';
import { ColorDropdown, getArbitraryColor } from './color-dropdown';
import { ItemProps, SortableOptionsProps } from './sortable-options';

const Row = styled(AntRow)`
  z-index: var(--zindex-popover-low);
`;

const StyledCol = styled(Col)`
  padding-left: 0 !important;
  padding-right: 0 !important;
`;

export const addStatusOption = (
  add: FormListOperation['add'],
  objectType: FieldSectionType,
): ((index: number) => void) => {
  return (index) =>
    add({
      group:
        objectType === FieldSectionType.Task
          ? taskStatusGroupOptionsLookup[TaskStatusGroup.Todo]
          : projectStatusGroupOptionsLookup[ProjectStatusGroup.Todo],
      deletable: true,
      editable: true,
      label: '',
      color: getArbitraryColor(index),
    } as FieldOption);
};

const SortableItem = SortableElement(
  ({ formField, form, add, remove, objectType, options }: ItemProps) => {
    const requiredMessage = useTranslation('input.required');
    const sameStatusMessage = useTranslation('form.validations.same_statuses');
    const currentOptionFieldPath = ['metaData', 'options', formField.name];
    const currentOption = form.getFieldValue(currentOptionFieldPath) as
      | FieldOption
      | undefined;
    const isEditable = currentOption?.editable;
    const isDeletable = currentOption?.deletable;

    return currentOption ? (
      <Row gutter={8}>
        <StyledCol flex="40px">
          <DragHandle />
        </StyledCol>
        <StyledCol flex="40px">
          <Form.Item
            {...formField}
            name={[formField.name, 'color']}
            fieldKey={[formField.fieldKey, 'color']}
            rules={[
              {
                required: true,
                message: requiredMessage,
              },
            ]}
            initialValue={getArbitraryColor(formField.name)}
          >
            <ColorDropdown
              option={currentOption}
              IconComponent={({ group, color }) => (
                <StatusIndicatorIcon
                  group={group ?? TaskStatusGroup.Todo}
                  color={color}
                />
              )}
            />
          </Form.Item>
        </StyledCol>
        <Col flex={isEditable ? '176px' : '352px'}>
          <Form.Item
            {...formField}
            name={[formField.name, 'label']}
            fieldKey={[formField.fieldKey, 'label']}
            rules={[
              {
                required: true,
                message: requiredMessage,
              },
              {
                message: sameStatusMessage,
                validator: (_, value) => {
                  const isOptionAvailable = options?.filter(
                    (option) =>
                      option.label.trim().toLowerCase() ===
                      value.trim().toLowerCase(),
                  );

                  return isOptionAvailable.length > 1
                    ? Promise.reject()
                    : Promise.resolve();
                },
              },
            ]}
            initialValue=""
          >
            <Input
              disabled={!isEditable}
              autoFocus
              onPressEnter={() => add(formField.name + 1)}
            />
          </Form.Item>
        </Col>
        {isEditable ? (
          <Col flex="176px">
            <Form.Item
              {...formField}
              name={[formField.name, 'group']}
              fieldKey={[formField.fieldKey, 'group']}
              rules={[
                {
                  required: true,
                  message: requiredMessage,
                },
              ]}
            >
              <StatusSelect
                menuPlacement="auto"
                menuPortalTarget={document.body}
                size="small"
                isClearable={false}
                options={
                  objectType === FieldSectionType.Task
                    ? taskStatusGroupOptions
                    : projectStatusGroupOptions
                }
                components={{
                  Option: StatusPickerOption,
                  SingleValue: StatusPickerOption,
                  Input: null,
                }}
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 'var(--zindex-popover-low)',
                  }),
                }}
                isSearchable={false}
              />
            </Form.Item>
          </Col>
        ) : null}

        {isDeletable && (
          <StyledCol flex="40px">
            <Form.Item shouldUpdate>
              <Tooltip
                mouseEnterDelay={1}
                title={<FormattedMessage id="settings.fields.remove_option" />}
              >
                <Button
                  type="text"
                  icon={<Close20 />}
                  onClick={() => remove(formField.name)}
                />
              </Tooltip>
            </Form.Item>
          </StyledCol>
        )}
      </Row>
    ) : null;
  },
);

export const SortableStatusOptions = SortableContainer(
  ({
    fields,
    form,
    add,
    remove,
    objectType,
    options,
  }: SortableOptionsProps) => {
    return (
      <div>
        {fields.map((formField) => (
          <SortableItem
            key={`formField-${formField.name}`}
            index={formField.name}
            formField={formField}
            form={form}
            add={add}
            remove={remove}
            objectType={objectType}
            options={options}
          />
        ))}
      </div>
    );
  },
);
